import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { buildAgentGuide } from "@/lib/agentApiGuide";

const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/ext/v1`;

function downloadGuide(agentName) {
  const md = buildAgentGuide(BASE, agentName);
  const url = URL.createObjectURL(new Blob([md], { type: "text/markdown" }));
  const a = Object.assign(document.createElement("a"), { href: url, download: `${agentName.replace(/\s+/g, "_")}_API_connection_guide.md` });
  a.click();
  URL.revokeObjectURL(url);
}

// Settings card: API keys for remote AI agents (read orders, add a customer-contact note, send the status email).
export function ExternalApiCard() {
  const qc = useQueryClient();
  const [name, setName] = useState("Jasmine Ai");
  const [grantStatusEmail, setGrantStatusEmail] = useState(false);
  const [secret, setSecret] = useState(null);
  const [logFor, setLogFor] = useState(null);
  const { data } = useQuery({ queryKey: ["api-keys"], queryFn: async () => (await api.get("/api-keys")).data });
  const create = useMutation({
    mutationFn: () => api.post("/api-keys", { name: name.trim(), scopes: ["orders:read", "orders:note", ...(grantStatusEmail ? ["orders:status_email"] : [])] }),
    onSuccess: (r) => { setSecret(r.data); setName(""); setGrantStatusEmail(false); qc.invalidateQueries({ queryKey: ["api-keys"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not create key"),
  });
  const setScopes = useMutation({
    mutationFn: ({ id, scopes }) => api.patch(`/api-keys/${id}/scopes`, { scopes }),
    onSuccess: (r) => { toast.success(r.data.scopes.includes("orders:status_email") ? "Status email granted — the agent can send it now" : "Status email permission removed"); qc.invalidateQueries({ queryKey: ["api-keys"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const revoke = useMutation({
    mutationFn: (id) => api.delete(`/api-keys/${id}`),
    onSuccess: () => { toast.success("Key revoked — the agent can no longer connect"); qc.invalidateQueries({ queryKey: ["api-keys"] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const { data: log } = useQuery({
    queryKey: ["api-key-log", logFor],
    queryFn: async () => (await api.get(`/api-keys/${logFor}/log`, { params: { limit: 50 } })).data?.log || [],
    enabled: !!logFor,
  });
  const keys = data?.keys || [];
  const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copied"));
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 space-y-4" data-testid="external-api-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-semibold text-base">🤖 External API — AI agents</h2>
          <p className="text-xs text-slate-500 mt-1">
            Order access for a remote agent (lookup by order number, email, phone or name), the right to log a customer-contact note and — when granted per key —
            to send the CRM's <b>Status email</b> to the verified customer (idempotent, one send per request). Keys are shown once, stored hashed,
            rate-limited ({data?.rate_limit_per_min || 120}/min) and every call is logged.
          </p>
        </div>
      </div>

      <div className="flex items-end gap-2 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <Label className="text-xs">Agent / key name</Label>
          <Input data-testid="api-key-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jasmine Ai" className="mt-1" />
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-700 pb-2 cursor-pointer select-none" data-testid="api-key-scope-status-email">
          <input type="checkbox" checked={grantStatusEmail} onChange={(e) => setGrantStatusEmail(e.target.checked)} className="accent-slate-900" data-testid="api-key-scope-status-email-checkbox" />
          Allow “Send status email”
        </label>
        <Button data-testid="api-key-create" disabled={!name.trim() || create.isPending} onClick={() => create.mutate()} className="bg-slate-900 hover:bg-slate-800 text-white">
          + Create key
        </Button>
      </div>

      <div className="divide-y divide-slate-100 border border-slate-200 rounded-md" data-testid="api-key-list">
        {keys.length === 0 && <div className="p-3 text-xs text-slate-500">No keys yet.</div>}
        {keys.map((k) => {
          const canSend = (k.scopes || []).includes("orders:status_email");
          return (
          <div key={k.id} className="p-3 text-sm" data-testid={`api-key-row-${k.id}`}>
            <div className="flex items-center gap-3 flex-wrap">
              <span className="font-semibold">{k.name}</span>
              <span className="font-mono text-xs text-slate-500">{k.key_prefix}</span>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${k.revoked_at ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`}>{k.revoked_at ? "REVOKED" : "ACTIVE"}</span>
              <span className="text-[11px] text-slate-500" data-testid={`api-key-scopes-${k.id}`}>{(k.scopes || []).map((s) => data?.scope_labels?.[s] || s).join(" · ")}</span>
              <span className="ml-auto text-[11px] text-slate-500">{k.calls || 0} calls{k.last_used_at ? ` · last ${new Date(k.last_used_at).toLocaleString()}` : " · never used"}</span>
              <button type="button" className="text-[11px] underline text-slate-600" onClick={() => setLogFor(logFor === k.id ? null : k.id)} data-testid={`api-key-log-${k.id}`}>log</button>
              {!k.revoked_at && (
                <Button size="sm" variant="outline" className={`h-7 text-xs ${canSend ? "border-amber-300 text-amber-800" : "border-sky-300 text-sky-800"}`}
                  disabled={setScopes.isPending}
                  onClick={() => {
                    const next = canSend ? (k.scopes || []).filter((s) => s !== "orders:status_email") : [...(k.scopes || []), "orders:status_email"];
                    if (!canSend && !window.confirm(`Allow "${k.name}" to send the Status email to customers? It can only send the CRM's standard status email, to the verified customer of a matched order, once per request.`)) return;
                    setScopes.mutate({ id: k.id, scopes: next });
                  }}
                  data-testid={`api-key-toggle-status-email-${k.id}`}>
                  {canSend ? "Remove status email" : "Grant status email"}
                </Button>
              )}
              {!k.revoked_at && (
                <Button size="sm" variant="outline" className="h-7 text-xs border-rose-300 text-rose-700" onClick={() => { if (window.confirm(`Revoke "${k.name}"? The agent will be cut off immediately.`)) revoke.mutate(k.id); }} data-testid={`api-key-revoke-${k.id}`}>
                  Revoke
                </Button>
              )}
            </div>
            <div className="text-[11px] text-slate-400 mt-0.5">created {new Date(k.created_at).toLocaleString()} by {k.created_by}{k.revoked_at ? ` · revoked ${new Date(k.revoked_at).toLocaleString()} by ${k.revoked_by}` : ""}{k.scopes_updated_at ? ` · permissions changed ${new Date(k.scopes_updated_at).toLocaleString()} by ${k.scopes_updated_by}` : ""}</div>
            {logFor === k.id && (
              <div className="mt-2 max-h-48 overflow-y-auto border border-slate-200 rounded bg-slate-50 text-[11px] font-mono" data-testid={`api-key-log-list-${k.id}`}>
                {(log || []).length === 0 && <div className="p-2 text-slate-500">No calls yet.</div>}
                {(log || []).map((l) => (
                  <div key={l.id} className="px-2 py-1 border-b border-slate-100 flex gap-2">
                    <span className="text-slate-500">{new Date(l.at).toLocaleString()}</span>
                    <span className={l.status >= 400 ? "text-rose-700" : "text-emerald-700"}>{l.status}</span>
                    <span>{l.method} {l.path.replace("/api/ext/v1", "")}{l.query?.q ? `?q=${l.query.q}` : ""}</span>
                    {l.po_number && <span className="text-slate-600">· {l.po_number}</span>}
                    {l.send_id && <span className="text-sky-700">· send {l.send_id.slice(0, 8)}{l.duplicate ? " (duplicate, not resent)" : ""}</span>}
                    {l.error && <span className="text-rose-600">· {l.error}</span>}
                    <span className="ml-auto text-slate-400">{l.ip}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          );
        })}
      </div>

      <details className="text-xs" data-testid="external-api-docs">
        <summary className="cursor-pointer font-semibold text-slate-700">How the agent connects (give this to the agent builder)</summary>
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => downloadGuide(name.trim() || "Jasmine Ai")} data-testid="agent-guide-download">
            ⬇ Download full instructions (.md)
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => copy(buildAgentGuide(BASE, name.trim() || "Jasmine Ai"))} data-testid="agent-guide-copy">
            Copy instructions
          </Button>
          <span className="text-[11px] text-slate-500">Endpoints, examples, conversation flow, privacy rules — send together with the key.</span>
        </div>
        <div className="mt-2 space-y-2 font-mono text-[11px] bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto">
          <div><span className="text-slate-400"># Base URL</span><br />{BASE}</div>
          <div><span className="text-slate-400"># Header on every call</span><br />Authorization: Bearer lp_live_…</div>
          <div><span className="text-slate-400"># Connection test</span><br />GET {BASE}/me</div>
          <div><span className="text-slate-400"># Find orders — order number, email, phone or name (min 3 chars)</span><br />GET {BASE}/orders/search?q=PO-5049<br />GET {BASE}/orders/search?q=mark@example.com<br />GET {BASE}/orders/search?q=4165551234</div>
          <div><span className="text-slate-400"># Full order (by PO / order number / id)</span><br />GET {BASE}/orders/PO-5049</div>
          <div><span className="text-slate-400"># Log what the client asked / was told (visible to staff in Notes)</span><br />POST {BASE}/orders/PO-5049/contact-note<br />{"{"} "note": "Client asked for ETA, told shipping this week", "channel": "chat", "customer_name": "Mark" {"}"}</div>
          <div><span className="text-slate-400"># Send the CRM Status email (needs the “Send status email” permission on the key) — idempotent</span><br />POST {BASE}/orders/PO-5049/status-email<br />Idempotency-Key: sdc-ticket-18422<br />{"{"} "customer_email": "mark@example.com", "request_ref": "sdc-ticket-18422" {"}"}<br /><span className="text-slate-400"># → {"{"} "ok": true, "send_id": "…", "duplicate": false, "estimate": "…", "sent_at": "…" {"}"} — a retry with the same key returns the same send_id with "duplicate": true</span></div>
          <div><span className="text-slate-400"># Check a send</span><br />GET {BASE}/sends/&lt;send_id&gt;</div>
          <div><span className="text-slate-400"># Status codes & labels</span><br />GET {BASE}/statuses</div>
        </div>
      </details>

      <Dialog open={!!secret} onOpenChange={(v) => { if (!v) setSecret(null); }}>
        <DialogContent data-testid="api-key-secret-dialog">
          <DialogHeader>
            <DialogTitle>🔑 Key for "{secret?.key?.name}"</DialogTitle>
            <DialogDescription>Copy it now — for security it is shown only this once. If it is lost, revoke it and create a new one.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all text-xs bg-slate-100 border border-slate-300 rounded p-2" data-testid="api-key-secret">{secret?.secret}</code>
            <Button size="sm" onClick={() => copy(secret?.secret)} data-testid="api-key-copy">Copy</Button>
          </div>
          <div className="text-[11px] text-slate-600">Header: <code>Authorization: Bearer {secret?.secret?.slice(0, 16)}…</code></div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
